/**
 * Screenplay feature module — shot-creation coverage-sync forwarding and the
 * reimport (baseline/current diff merge) verbs, plus their fire-and-forget
 * outbound senders. Case bodies and senders moved verbatim from
 * embeddedBridge.ts.
 */

import i18next from 'i18next';
import type { HandlerMap } from '../dispatch';
import { enqueueSendToNative, MAX_BATCH_BYTES } from '../transport';
// Static, unlike the screenplayImportStore imports below: importChecklistStore imports nothing but
// zustand, so it cannot close the store↔bridge cycle those dynamic imports exist to avoid. See the
// same note in features/timeline.ts, which owns the other half of this hand-off.
import { useImportChecklistStore } from '@/store/importChecklistStore.ts';
// Static for the same reason features/timeline.ts imports it statically: projectStore imports no
// bridge module, so it cannot close the store↔bridge cycle. Read here ONLY for the pre-apply
// baseline version the latch is armed against.
import { useProjectStore } from '@/store/projectStore.ts';

export interface ShotCreationResultMessage {
  type: 'shotCreationResult';
  payload: { shotId: number; shotNumber: string };
}

export interface StartReimportMessage {
  type: 'startReimport';
  payload: {
    baselineHash: string;
    baseline: unknown | null;
    current: unknown;
    parsedScreenplay: unknown;
  };
}

export interface ReimportCompleteMessage {
  type: 'reimportComplete';
  payload: { success: boolean };
}

/**
 * Reimport failure (Phase C wire contract). Unity emits this for the picker/parse phase and
 * alongside an apply failure; `reimportComplete` still carries only `{success}`.
 *
 * Codes Unity emits today: `picker-cancelled` and `session-torn-down` (NEITHER is an error — the
 * store returns the UI to its pre-request state silently for both), `unsupported-format`,
 * `unreadable-file`, `parse-failure`, `version-rejected`. The field is typed as a plain string ON
 * PURPOSE: a code from a newer Unity build must degrade to a generic message that shows the raw
 * code, never be dropped. `detail` is optional plain text (filenames, parser output), rendered as-is.
 *
 * `session-torn-down` is the deterministic release for the web-side picker blocker: Unity's
 * teardown paths (panel exit, webview cleanup, a genuine project boot) used to cancel the picker
 * session and tell the web NOTHING, so an overlay bound to `importRequestPending` had only its
 * failsafe timer to fall back on. Which side owns "is this an error" is the store's call, not this
 * module's — the normalisation below is code-blind on purpose, and the two silent codes live
 * together in screenplayImportStore's SILENT_RESET_CODES.
 *
 * RESERVED, WEB-OWNED: `payload-too-large` (PAYLOAD_TOO_LARGE_CODE below) is raised by this
 * module's own pre-send guard and travels no wire at all. Unity must never emit it — if a native
 * build ever needs to report an oversized payload it needs a DIFFERENT code, or the two meanings
 * become indistinguishable in the one place a user reads them.
 */
export interface ReimportErrorMessage {
  type: 'reimportError';
  payload: { code?: unknown; detail?: unknown };
}

export type ScreenplayMessage =
  | ShotCreationResultMessage
  | StartReimportMessage
  | ReimportCompleteMessage
  | ReimportErrorMessage;

export const screenplayHandlers = {
  shotCreationResult: (msg: ShotCreationResultMessage) => {
    import('@/bridge/coverageSyncBridge.ts').then(m => m.handleShotCreationResult(msg.payload));
  },
  startReimport: (msg: StartReimportMessage) => {
    const { baselineHash, baseline, current, parsedScreenplay } = msg.payload;
    import('@/store/screenplayImportStore').then(({ useScreenplayImportStore }) => {
      useScreenplayImportStore.getState().startSession({
        baselineHash, baseline, current, parsedScreenplay,
      });
    });
  },
  reimportComplete: (msg: ReimportCompleteMessage) => {
    // 20260914 (owner) — SCRIPT IMPORT CHECKLIST, revision arm. A successful in-project import is
    // the only thing that knows a revision just landed, but it is the wrong moment to show the
    // card: the project store still holds the PRE-apply baseline, and the row set turns on that
    // baseline's version number. So this only sets a latch; Unity's post-apply repush
    // (`setSqlitePath { refresh: true }`) consumes it and starts the run, by which time the new
    // baseline is loaded. features/timeline.ts owns that half.
    //
    // The latch carries the PRE-APPLY baseline version — the one the store still holds at this
    // moment — because that is what lets the consume tell the apply's own repush from an unrelated
    // one that merely happened to resolve first (importChecklistStore's armOnNextLoad note). `?? 0`
    // covers the project with no import history at all: a first import into a blank project can
    // travel this machinery, arms 0, and is consumed by the load that brings version 1.
    //
    // NOTHING ON FAILURE. The session stays open in its visible error state (see the contract note
    // on ReimportCompleteMessage and screenplayImportStore's banner rules) and the project is
    // unchanged — there is no import to run a checklist over. Written synchronously, before the
    // dynamic import below, so it cannot lose a race with a repush that arrives immediately after.
    if (msg.payload.success) {
      const armedVersion = useProjectStore.getState().project?.baseline?.versionNumber ?? 0;
      useImportChecklistStore.getState().setArmOnNextLoad(armedVersion);
    }
    import('@/store/screenplayImportStore').then(({ useScreenplayImportStore }) => {
      const store = useScreenplayImportStore.getState();
      // 20260930 - SUCCESS TOAST. endSession() drops the apply blocker and the review in one write,
      // so without this a finished apply (behind a blocker that can last minutes) ends in silence:
      // the checklist card is a to-do list, not a confirmation, and is parked in many states. Read
      // revision-vs-first-import BEFORE endSession, which clears the baseline it is decided by.
      // Toast is loaded lazily, as in features/imagePick.ts: it is a React component module and
      // stays out of the bridge's eager graph. Never on failure: the review's error banner owns that.
      const wasRevision = store.baseline != null;
      store.setApplyStatus(msg.payload.success ? 'success' : 'error');
      if (msg.payload.success) {
        store.endSession();
        void import('@/components/Toast.tsx').then(({ showToast }) => showToast(
          i18next.t(wasRevision
            ? 'screenplayImport:reimportAppliedToast'
            : 'screenplayImport:firstImportAppliedToast'),
          'success',
        ));
      }
    });
    // NO GUIDE ARMING HERE any more (20260904). A successful in-project import used to start the
    // first-import guide from this handler; that guide is retired, and the Script tab's coach
    // marks that replaced it arm on first ENTRY to the tab, from the component itself
    // (ScreenplayManager/ScriptOrient.tsx) — nothing on the wire triggers them.
  },
  reimportError: (msg: ReimportErrorMessage) => {
    // Normalised at the edge so the store and the UI never deal in malformed input: a missing
    // or blank code becomes the literal 'unknown' (which renders the generic message rather
    // than an empty parenthesis), and a blank detail becomes absent.
    //
    // Trimming is what REGISTERS a new wire code here: `session-torn-down` (and every other code)
    // reaches the store as its exact string, and the store decides whether it is visible. Adding a
    // per-code branch in this module would split that decision across two files.
    const rawCode = typeof msg.payload?.code === 'string' ? msg.payload.code.trim() : '';
    const code = rawCode === '' ? 'unknown' : rawCode;
    const rawDetail = typeof msg.payload?.detail === 'string' ? msg.payload.detail.trim() : '';
    const detail = rawDetail === '' ? undefined : rawDetail;
    import('@/store/screenplayImportStore').then(({ useScreenplayImportStore }) => {
      useScreenplayImportStore.getState().reportImportError(code, detail);
    });
  },
} satisfies HandlerMap;

// ─── Screenplay reimport senders ─────────────────────────────────────────

/**
 * WEB-OWNED reimport error code — Unity must NEVER emit it (see the ReimportErrorMessage contract
 * note above). It is raised only by the pre-send guard below, which is why it needs no wire
 * representation at all.
 */
export const PAYLOAD_TOO_LARGE_CODE = 'payload-too-large';

/** DECIMAL MB (÷1e6), matching the "MB" label — the binary divisor would print 1.43 MB for a
 *  1,500,000-byte limit and start every support conversation with a wrong number. Exact bytes ride
 *  along because that is the figure worth comparing against a log or another measurement. */
const asMB = (bytes: number): string => `${(bytes / 1_000_000).toFixed(2)} MB (${bytes} bytes)`;

/**
 * Send the merge plan, unless it is too big to survive the trip.
 *
 * Measured 20260805 on the real construction: post-D1 the payload is script-proportional at ~5.34
 * URL-bytes per character of screenplay text, so a normal feature (110-120 pages) produces a
 * 1.6-1.8 MB navigation. The path it takes has NO size check — `__drainOutbox`'s single-message
 * fast path returns before MAX_BATCH_BYTES is consulted, and the batch loop always takes the first
 * message whatever its size — and an over-long `previspro://` navigation fails the way every other
 * dropped navigation does: silently, leaving the apply pending forever with nothing rendered.
 *
 * So refuse before navigating. The measurement is of what the TRANSPORT actually ships, not of the
 * plan object: sendToNative stringifies `{action, ...payload}` and the drain percent-encodes that
 * into the URL, which expands JSON by ~1.53x — measuring the raw object would under-count by a
 * third.
 *
 * @returns true if the message was handed to the transport; false if it was refused, in which case
 *          the C4 error surface has been given the reason and the caller must NOT enter its
 *          submitting state (nothing was sent, so nothing will ever answer).
 */
export function sendApplyReimport(mergePlan: unknown): boolean {
  const msg = JSON.stringify({ action: 'applyReimport', mergePlan });
  // Percent-encoding emits ASCII only, so the encoded URL's length IS its byte count.
  const urlBytes = `previspro://bridge?msg=${encodeURIComponent(msg)}`.length;

  if (urlBytes > MAX_BATCH_BYTES) {
    // Size first, limit second, both in MB: this string is what a support conversation starts from.
    const detail = `Merge payload is ${asMB(urlBytes)}; the limit is ${asMB(MAX_BATCH_BYTES)}.`;
    import('@/store/screenplayImportStore').then(({ useScreenplayImportStore }) => {
      const store = useScreenplayImportStore.getState();
      store.reportImportError(PAYLOAD_TOO_LARGE_CODE, detail);
      // Same visible state as any apply failure: session stays, banner shows, Cancel exits.
      store.setApplyStatus('error');
    });
    return false;
  }

  enqueueSendToNative('applyReimport', { mergePlan });
  return true;
}

export function sendCancelReimport(): void {
  enqueueSendToNative('cancelReimport', {});
}

export function sendRequestImportNewVersion(): void {
  enqueueSendToNative('requestImportNewVersion', {});
}

// ─── Screenplay CONTENT import (the Script tab's file drop) ──────────────
//
// The picker route asks Unity to open a native file dialog and hands back a path. A drop has no
// path to hand back — the file lives in the page — so the bytes themselves go over, Unity writes
// them to a temp file, and BOTH doors converge on the same import session from there. Nothing
// inbound changes: success is the existing `startReimport` push, failure the existing
// `reimportError`, and the store's importRequestPending lifecycle resolves identically.

/**
 * Emitted by Unity when the payload exceeds its own 20 MB (base64 char count) ceiling — and
 * raised WEB-SIDE too, by the drop's defensive size gate, which refuses a file before spending
 * seconds encoding one that is certain to be rejected. Unlike PAYLOAD_TOO_LARGE_CODE this code
 * DOES travel the wire; the two meanings are the same one, arrived at from either end.
 */
export const SCREENPLAY_TOO_LARGE_CODE = 'screenplay-too-large';

/**
 * The transfer itself went wrong: an unparseable message, bad base64, an unusable fileName, bad
 * chunk indices, a 60 s stall between chunks, or a temp write that failed. Raised web-side as
 * well, for the one failure that can only happen here — the File could not be read or encoded.
 * Deliberately distinct from `parse-failure`: nothing has been parsed yet, and the advice
 * differs (try again / use the Import button, rather than re-save the script).
 */
export const SCREENPLAY_CONTENT_FAILED_CODE = 'screenplay-content-failed';

/** 512 KB of base64 per chunk — the same figure `setStaticImageChunk` has shipped with, and the
 *  size `BridgeActionReader`'s fast prefix path exists to keep cheap. */
export const SCREENPLAY_CONTENT_CHUNK_BASE64_CHARS = 512 * 1024;

/**
 * The byte count that encodes to EXACTLY that many base64 characters: 4 chars per 3 bytes, and
 * 393216 is a multiple of 3, so a full slice encodes unpadded and the seams stay byte-aligned.
 *
 * It doubles as the single-vs-chunked threshold, which the contract states in base64 chars. The
 * two are the same test — base64 length is 4·ceil(n/3), so "≤ 512 KB of base64" is exactly
 * "≤ 393216 bytes" — and testing bytes avoids encoding the whole file once just to measure it.
 */
export const SCREENPLAY_CONTENT_CHUNK_BYTES = (SCREENPLAY_CONTENT_CHUNK_BASE64_CHARS / 4) * 3;

/** String.fromCharCode is applied a window at a time: spreading a multi-megabyte Uint8Array into
 *  one call overflows the argument stack. 32 KB is the size the platform's own examples use. */
const BASE64_STRIDE = 0x8000;

function bytesToBase64(bytes: Uint8Array): string {
  let binary = '';
  for (let i = 0; i < bytes.length; i += BASE64_STRIDE) {
    binary += String.fromCharCode(...bytes.subarray(i, i + BASE64_STRIDE));
  }
  return btoa(binary);
}

/**
 * Hand a dropped screenplay file to Unity.
 *
 * `fileName` MUST carry the real extension — `ScreenplayParserFactory` dispatches on it, and the
 * name also becomes the imported version's displayed sourceFilename.
 *
 * EVERY CHUNK IS ENCODED INDEPENDENTLY, which is the opposite of what audioSyncBridge does and is
 * the difference that matters: Unity decodes each chunk as it arrives and copies the resulting
 * bytes into the assembly buffer, so a whole-file encoding sliced into 512 KB pieces would put a
 * partial base64 group at every seam and fail to decode from chunk one onward.
 */
export function sendImportScreenplayContent(fileName: string, bytes: Uint8Array): void {
  if (bytes.length <= SCREENPLAY_CONTENT_CHUNK_BYTES) {
    enqueueSendToNative('importScreenplayContent', { fileName, data: bytesToBase64(bytes) });
    return;
  }

  const totalChunks = Math.ceil(bytes.length / SCREENPLAY_CONTENT_CHUNK_BYTES);
  for (let i = 0; i < totalChunks; i++) {
    const slice = bytes.subarray(i * SCREENPLAY_CONTENT_CHUNK_BYTES, (i + 1) * SCREENPLAY_CONTENT_CHUNK_BYTES);
    enqueueSendToNative('importScreenplayContentChunk', {
      fileName,
      chunkIndex: i,
      totalChunks,
      data: bytesToBase64(slice),
    });
  }
}
